import React, { useEffect, useState } from 'react';
import AutoRechargePanel from './AutoRechargePanel';
import { canConfigureAutoRecharge } from './autoRechargeRules';
import { actingRole } from '../../utils/roleGrants';
import TopUpPanel from './TopUpPanel';
import TaxInvoicesPanel from './TaxInvoicesPanel';
import RefusalCountsPanel from './RefusalCountsPanel';
import { isFeatureEnabled } from 'components/Layout/navVisibility';
import { FileText } from 'lucide-react';
import { billingService } from '../../api/services/billing.service';
import { formatMoney } from '../../types/billing';
import { useAuth } from 'hooks/useAuth';
import { istMonth } from './closeBooksRules';
import OrgFilterSelect from './OrgFilterSelect';
import { useLatestRequest } from './latestRequest';
import { isPlatformStaff } from './billingReportRules';
import {
  MyBucket, MyReceivables, MySummary, MyWallet, formatMinutes, payableHeading, periodCaption, recentMonths,
  runsOutText, taxHeader,
} from './myBillingRules';

const card = 'bg-white dark:bg-gray-800 rounded-lg shadow p-4 space-y-4';
const h2 = 'text-lg font-semibold text-gray-900 dark:text-white';
const muted = 'text-sm text-gray-500 dark:text-gray-400';
const th = 'px-3 py-2 text-xs font-medium text-gray-500 dark:text-gray-300 uppercase whitespace-nowrap text-left';
const td = 'px-3 py-2 text-sm text-gray-900 dark:text-gray-100 whitespace-nowrap';
const tdNum = `${td} text-right tabular-nums`;
const inputCls =
  'px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md text-sm';

const Stat: React.FC<{ label: string; value: string; hint?: string }> = ({ label, value, hint }) => (
  <div className="rounded-md border border-gray-200 dark:border-gray-700 p-3">
    <div className="text-xs uppercase text-gray-500 dark:text-gray-400">{label}</div>
    <div className="text-lg font-semibold text-gray-900 dark:text-white tabular-nums">{value}</div>
    {hint && <div className="text-xs text-gray-500 dark:text-gray-400">{hint}</div>}
  </div>
);

const BucketTable: React.FC<{ title: string; rows: MyBucket[]; currency: string }> = ({ title, rows, currency }) => (
  <div className="overflow-x-auto">
    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
      <thead>
        <tr>
          <th className={th}>{title}</th>
          <th className={`${th} text-right`}>Charges</th>
          <th className={`${th} text-right`}>Minutes</th>
          <th className={`${th} text-right`}>Amount</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
        {rows.length === 0 && (
          <tr><td className={td} colSpan={4}>No usage in this period</td></tr>
        )}
        {rows.map((b) => (
          <tr key={b.key}>
            <td className={td}>{b.label}</td>
            <td className={tdNum}>{b.charges}</td>
            <td className={tdNum}>{formatMinutes(b.billedMinutes)}</td>
            <td className={tdNum}>{formatMoney(b.amount, currency)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const WalletCard: React.FC<{ w: MyWallet }> = ({ w }) => {
  if (!w.hasAccount) {
    return (
      <section className={card} aria-label="Wallet">
        <h2 className={h2}>Wallet</h2>
        <p className={muted}>No billing account: usage is billed to the organisation above you.</p>
      </section>
    );
  }
  const cur = w.currency || 'INR';
  if (!w.prepaid) {
    return (
      <section className={card} aria-label="Wallet">
        <h2 className={h2}>Wallet</h2>
        <p className={muted} data-testid="postpaid-note">{w.note || 'Postpaid — billed monthly'}</p>
        {w.creditLimit !== null && w.creditLimit !== undefined && (
          <p className={muted}>Credit limit {formatMoney(w.creditLimit, cur)}</p>
        )}
      </section>
    );
  }
  return (
    <section className={card} aria-label="Wallet">
      <h2 className={h2}>Wallet (prepaid)</h2>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Available" value={formatMoney(w.available, cur)} hint="Balance less calls in progress" />
        <Stat label="Balance" value={formatMoney(w.balance, cur)}
          hint={w.openHolds ? `${formatMoney(w.openHolds, cur)} held` : undefined} />
        <Stat label="Burn rate" value={`${formatMoney(w.burnPerDay, cur)}/day`} hint="Average of the last 7 days" />
        <Stat label="Runs out" value={w.runsOutOn ? w.runsOutOn : '—'} hint={runsOutText(w)} />
      </div>
      {(w.overdraft ?? 0) > 0 && (
        <p className="text-sm text-red-600 dark:text-red-400">
          Overdraft {formatMoney(w.overdraft, cur)}: the next top-up repays this first.
        </p>
      )}
      {(w.expiringSoonAmount ?? 0) > 0 && (
        <p className="text-sm text-amber-700 dark:text-amber-400">
          {formatMoney(w.expiringSoonAmount, cur)} of credit expires in the next 30 days.
        </p>
      )}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead>
            <tr>
              <th className={th}>Credit</th>
              <th className={`${th} text-right`}>Granted</th>
              <th className={`${th} text-right`}>Remaining</th>
              <th className={th}>Expires</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {w.grants.length === 0 && <tr><td className={td} colSpan={4}>No credit grants</td></tr>}
            {w.grants.map((g) => (
              <tr key={g.id}>
                <td className={td}>{g.source}{g.description ? ` — ${g.description}` : ''}</td>
                <td className={tdNum}>{formatMoney(g.originalAmount, cur)}</td>
                <td className={tdNum}>{formatMoney(g.remainingAmount, cur)}</td>
                <td className={td}>{g.expiresAt ? g.expiresAt.slice(0, 10) : 'Never'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {w.recentTopUps.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Recent top-ups</h3>
          <ul className={muted}>
            {w.recentTopUps.map((t) => (
              <li key={t.transactionId}>{t.occurredAt?.slice(0, 10)} · {formatMoney(t.amount, cur)}</li>
            ))}
          </ul>
        </div>
      )}
      {w.alerts?.tiers && w.alerts.tiers.length > 0 && (
        <div>
          <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Low-balance alerts</h3>
          <ul className={muted}>
            {w.alerts.tiers.map((t) => (
              <li key={t.tier}>
                {t.tier} at {formatMoney(t.thresholdAmount, cur)}: {t.crossed ? 'crossed' : 'not reached'}
                {t.status ? ` (${t.status.toLowerCase()})` : ''}
              </li>
            ))}
          </ul>
        </div>
      )}
      {w.autoRecharge && (
        <div className="rounded-md border border-dashed border-gray-300 dark:border-gray-600 p-3" data-testid="auto-recharge">
          <div className="text-sm text-gray-700 dark:text-gray-300">
            Auto-recharge: {w.autoRecharge.enabled ? 'requested' : 'off'}
            {w.autoRecharge.threshold !== null ? ` · below ${formatMoney(w.autoRecharge.threshold, cur)}` : ''}
            {w.autoRecharge.amount !== null ? ` · add ${formatMoney(w.autoRecharge.amount, cur)}` : ''}
          </div>
          <div className={muted}>{w.autoRecharge.note}</div>
        </div>
      )}
    </section>
  );
};

/**
 * My billing: one page for providers, businesses and platform staff, for the CURRENT
 * organisation (so "Acting as this business" shows that business's own bill). Platform
 * staff may pick any organisation. What appears is decided by the server per organisation:
 * a business never receives its provider's upstream figures.
 */
const MyBilling: React.FC = () => {
  const { user, currentOrganization } = useAuth();
  const staff = isPlatformStaff((currentOrganization as any)?.role || (user as any)?.role);
  const [month, setMonth] = useState(istMonth());
  const [orgId, setOrgId] = useState(currentOrganization?.id || '');
  const [summary, setSummary] = useState<MySummary | null>(null);
  const [receivables, setReceivables] = useState<MyReceivables | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const beginRequest = useLatestRequest();

  // Follow the organisation switcher.
  useEffect(() => {
    setOrgId(currentOrganization?.id || '');
  }, [currentOrganization?.id]);

  useEffect(() => {
    const isLatest = beginRequest();
    setLoading(true);
    const params = { month, organizationId: orgId || undefined };
    billingService.getMySummary(params).then(async (r) => {
      if (!isLatest()) return;
      if (!r.success || !r.data) {
        setSummary(null);
        setReceivables(null);
        setError(r.error || 'Failed to load your billing');
        setLoading(false);
        return;
      }
      setSummary(r.data);
      setError(null);
      if (r.data.hasReceivable) {
        const rec = await billingService.getMyBusinesses(params);
        if (!isLatest()) return;
        setReceivables(rec.success && rec.data ? rec.data : null);
      } else {
        setReceivables(null);
      }
      setLoading(false);
    });
  }, [month, orgId, beginRequest]);

  const download = async () => {
    setBusy(true);
    setDownloadError(null);
    const r = await billingService.downloadMyStatement({ month, organizationId: orgId || undefined });
    if (!r.ok) setDownloadError(r.error);
    setBusy(false);
  };

  const cur = summary?.payable.account?.currency || summary?.wallet?.currency || 'INR';
  const c = summary?.payable.charges;
  const showByBusiness = summary && summary.view !== 'BUSINESS';
  const maxDay = Math.max(1, ...(c?.daily || []).map((d) => d.amount));

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">My billing</h1>
          <p className={muted}>
            {summary ? `${summary.organizationName} · ${periodCaption(summary.period)}` : 'What you owe, by month (IST)'}
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          {staff && <OrgFilterSelect value={orgId} onChange={setOrgId} allLabel="Choose an organization" required />}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" htmlFor="my-billing-month">Month</label>
            <select id="my-billing-month" className={inputCls} value={month} onChange={(e) => setMonth(e.target.value)}>
              {recentMonths().map((m) => <option key={m} value={m}>{m}{m === istMonth() ? ' (to date)' : ''}</option>)}
            </select>
          </div>
          <button type="button" onClick={download} disabled={busy || !summary}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-indigo-600 text-white text-sm disabled:opacity-50">
            <FileText className="w-4 h-4" /> Download .xlsx
          </button>
        </div>
      </div>
      {downloadError && <p className="text-sm text-red-600">{downloadError}</p>}
      {error && <p className="text-sm text-red-600" role="alert">{error}</p>}
      {loading && !summary && <p className={muted}>Loading…</p>}

      {summary && c && (
        <>
          <section className={card} aria-label="Payable">
            <div>
              <h2 className={h2}>{payableHeading(summary)}</h2>
              <p className={muted}>
                {c.tax.note}
                {summary.payable.documentType === 'INTERNAL_STATEMENT' ? ' · internal statement' : ''}
              </p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <Stat label="Charges" value={String(c.charges)} />
              <Stat label="Minutes" value={formatMinutes(c.billedMinutes)} />
              <Stat label="Base" value={formatMoney(c.base, cur)} />
              <Stat label={taxHeader(c.tax)} value={formatMoney(c.taxAmount, cur)} />
              <Stat label="Total" value={formatMoney(c.total, cur)} />
            </div>
            {summary.payable.account && (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3" aria-label="Account movement">
                <Stat label="Opening balance" value={formatMoney(summary.payable.account.openingBalance, cur)} />
                <Stat label="Payments / top-ups" value={formatMoney(summary.payable.account.paymentsAndTopUps, cur)} />
                <Stat label="Usage" value={formatMoney(-summary.payable.account.usage, cur)} />
                <Stat label="Other" value={formatMoney(summary.payable.account.other, cur)} />
                <Stat label="Closing balance" value={formatMoney(summary.payable.account.closingBalance, cur)} />
              </div>
            )}
            <div className="grid md:grid-cols-2 gap-4">
              {showByBusiness && <BucketTable title="Business" rows={c.byBusiness} currency={cur} />}
              <BucketTable title="Service" rows={c.byServiceType} currency={cur} />
              {/* Empty for a business: the server never tells it which carrier carried its calls. */}
              {c.byCarrier.length > 0 && <BucketTable title="Carrier" rows={c.byCarrier} currency={cur} />}
            </div>
            {c.daily.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">By day (IST)</h3>
                <div className="space-y-1">
                  {c.daily.map((d) => (
                    <div key={d.day} className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
                      <span className="w-20 tabular-nums">{d.day}</span>
                      <div className="flex-1 h-3 bg-gray-100 dark:bg-gray-700 rounded">
                        <div className="h-3 bg-indigo-500 rounded" style={{ width: `${Math.max(1, (d.amount / maxDay) * 100)}%` }} />
                      </div>
                      <span className="w-28 text-right tabular-nums">{formatMoney(d.amount, cur)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div>
              <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Invoices · outstanding {formatMoney(summary.payable.outstanding, cur)}
              </h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead>
                    <tr>
                      <th className={th}>Invoice</th>
                      <th className={th}>Status</th>
                      <th className={th}>Date</th>
                      <th className={th}>Due</th>
                      <th className={`${th} text-right`}>Total</th>
                      <th className={`${th} text-right`}>Outstanding</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {[...summary.payable.invoices,
                      ...summary.payable.outstandingInvoices.filter((o) => !summary.payable.invoices.some((i) => i.id === o.id))]
                      .map((i) => (
                        <tr key={i.id}>
                          <td className={td}>{i.invoiceNumber}</td>
                          <td className={td}>{i.status}</td>
                          <td className={td}>{i.invoiceDate}</td>
                          <td className={td}>{i.dueDate}</td>
                          <td className={tdNum}>{formatMoney(i.total, i.currency || cur)}</td>
                          <td className={tdNum}>{formatMoney(i.outstanding, i.currency || cur)}</td>
                        </tr>
                      ))}
                    {summary.payable.invoices.length === 0 && summary.payable.outstandingInvoices.length === 0 && (
                      <tr><td className={td} colSpan={6}>No invoices in this period</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              {summary.payable.payments.length > 0 && (
                <ul className={`${muted} mt-2`}>
                  {summary.payable.payments.map((p) => (
                    <li key={p.id}>Payment {p.reference} · {p.paymentDate?.slice(0, 10)} · {formatMoney(p.amount, p.currency || cur)}</li>
                  ))}
                </ul>
              )}
            </div>
          </section>

          {/* Hidden while the GST engine is off (its API answers 404). */}
          <TaxInvoicesPanel organizationId={summary.organizationId} />

          <WalletCard w={summary.wallet} />

          {summary.wallet?.prepaid && (
            // One-time top-up link. Prepaid wallets only, like auto-recharge; the server
            // flag decides whether it is offered, and the card says so quietly when not.
            <TopUpPanel organizationId={summary.organizationId} currency={summary.wallet.currency} />
          )}

          {summary.wallet?.prepaid && (
            // Owners and above may change it (owner, 2026-10-25); business admins read only.
            // The server decides again and the panel shows its refusal if not allowed.
            <AutoRechargePanel organizationId={summary.organizationId}
              editable={canConfigureAutoRecharge(actingRole({ user, currentOrganization }))} />
          )}

          {/* Ships dark: only with REACT_APP_ENABLE_FEATURES=refusal-counts in the build. */}
          {isFeatureEnabled('refusal-counts') && <RefusalCountsPanel organizationId={summary.organizationId} />}

          {receivables && (
            <section className={card} aria-label="Receivable">
              <div>
                <h2 className={h2}>Receivable from my businesses</h2>
                <p className={muted}>
                  What each business is charged at your prices, and your margin over what you pay upstream for the same usage.
                </p>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead>
                    <tr>
                      <th className={th}>Business</th>
                      <th className={`${th} text-right`}>Minutes</th>
                      <th className={`${th} text-right`}>Charged</th>
                      <th className={`${th} text-right`}>Tax</th>
                      <th className={`${th} text-right`}>Total</th>
                      <th className={`${th} text-right`}>Your cost</th>
                      <th className={`${th} text-right`}>Margin</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {receivables.businesses.length === 0 && (
                      <tr><td className={td} colSpan={7}>No business usage in this period</td></tr>
                    )}
                    {receivables.businesses.map((b) => (
                      <tr key={b.businessId}>
                        <td className={td}>{b.businessName}{b.metered ? '' : ' (not metered)'}</td>
                        <td className={tdNum}>{formatMinutes(b.billedMinutes)}</td>
                        <td className={tdNum}>{formatMoney(b.amount, cur)}</td>
                        <td className={tdNum} title={b.tax.note}>{formatMoney(b.taxAmount, cur)}</td>
                        <td className={tdNum}>{formatMoney(b.total, cur)}</td>
                        <td className={tdNum}>{formatMoney(b.upstreamCost, cur)}</td>
                        <td className={tdNum}>{formatMoney(b.margin, cur)}</td>
                      </tr>
                    ))}
                  </tbody>
                  {receivables.businesses.length > 0 && (
                    <tfoot>
                      <tr className="font-semibold">
                        <td className={td}>Total</td>
                        <td className={tdNum}>{formatMinutes(receivables.billedMinutes)}</td>
                        <td className={tdNum}>{formatMoney(receivables.amount, cur)}</td>
                        <td className={tdNum}>{formatMoney(receivables.taxAmount, cur)}</td>
                        <td className={tdNum}>{formatMoney(receivables.total, cur)}</td>
                        <td className={tdNum}>{formatMoney(receivables.upstreamCost, cur)}</td>
                        <td className={tdNum}>{formatMoney(receivables.margin, cur)}</td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
};

export default MyBilling;
